import { useCallback, useEffect, useState } from "react";
import { useBlocker } from "react-router-dom";

/**
 * Iter 82 — Hook para detectar cambios sin guardar.
 *
 * Uso:
 *   const unsaved = useUnsavedChanges({ dirty, onSaveAndExit });
 *   ...
 *   {unsaved.dialog}
 *
 *   `unsaved.dialog` renderiza automáticamente `<UnsavedChangesDialog />`
 *   cuando React Router intercepta una navegación estando `dirty=true`.
 *
 * Cubre:
 *   1) Navegación interna (link/menu) vía `useBlocker` de react-router 7.
 *   2) Cierre de pestaña / recarga / atrás del navegador vía `beforeunload`
 *      (el navegador muestra su propio prompt genérico — no podemos
 *      personalizarlo por seguridad).
 *   3) Botón atrás dentro del historial (mismo useBlocker).
 *
 * `onSaveAndExit` es una función async que guarda y devuelve `true` en éxito
 * o `false` en fallo (para permitir "Guardar y salir" del diálogo).
 */
export default function useUnsavedChanges({ dirty, onSaveAndExit }) {
  const blocker = useBlocker(dirty);
  const [saving, setSaving] = useState(false);

  // 1) beforeunload — bloquea cierre/recarga cuando hay cambios
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const proceed = useCallback(() => {
    blocker.proceed?.();
  }, [blocker]);

  const reset = useCallback(() => {
    blocker.reset?.();
  }, [blocker]);

  const saveAndExit = useCallback(async () => {
    if (!onSaveAndExit) {
      proceed();
      return;
    }
    setSaving(true);
    try {
      const ok = await onSaveAndExit();
      if (ok !== false) proceed();
      else reset();
    } finally {
      setSaving(false);
    }
  }, [onSaveAndExit, proceed, reset]);

  const showing = blocker.state === "blocked";
  const dialog = showing ? (
    <UnsavedChangesDialog
      saving={saving}
      onSaveAndExit={saveAndExit}
      onDiscard={proceed}
      onCancel={reset}
    />
  ) : null;

  return { dialog, showing };
}


export function UnsavedChangesDialog({ saving, onSaveAndExit, onDiscard, onCancel }) {
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="unsaved-dialog"
    >
      <div
        className="bg-white border-2 border-[#111110] shadow-hard w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">// Iter 82</div>
        <div className="font-display font-bold text-lg text-[#111110] mt-1">
          Tienes cambios sin guardar
        </div>
        <p className="text-sm text-[#525252] mt-3 leading-relaxed">
          Hay cambios en esta ficha que todavía no se han guardado. ¿Qué quieres hacer?
        </p>
        <div className="mt-5 flex flex-col sm:flex-row justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            data-testid="unsaved-dialog-cancel"
            className="px-4 py-2 text-xs uppercase tracking-wider border border-[#E5E5E0] hover:bg-[#F7F7F4]"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onDiscard}
            disabled={saving}
            data-testid="unsaved-dialog-discard"
            className="px-4 py-2 text-xs uppercase tracking-wider border border-red-500 text-red-700 hover:bg-red-50"
          >
            Salir sin guardar
          </button>
          <button
            type="button"
            onClick={onSaveAndExit}
            disabled={saving}
            data-testid="unsaved-dialog-save"
            className="px-4 py-2 text-xs uppercase tracking-wider bg-[#6E8B3D] hover:bg-[#556C2E] text-white font-semibold disabled:bg-[#A8A8A0]"
          >
            {saving ? "Guardando…" : "Guardar y salir"}
          </button>
        </div>
      </div>
    </div>
  );
}
